import { type ReactNode } from "react"

export type BadgeVariant =
  | "energy-star"
  | "top-rated"
  | "lowest-price"
  | "best-seller"
  | "sale"
  | "new"
  | "exclusive"
  | "custom"

interface BadgePillProps {
  variant: BadgeVariant
  /** Override label text (defaults to variant name) */
  label?: string
  /** Custom background color class (only used with variant="custom") */
  colorClass?: string
  /** Custom icon (only used with variant="custom") */
  icon?: ReactNode
}

const VARIANT_CONFIG: Record<
  Exclude<BadgeVariant, "custom">,
  { label: string; colorClass: string; icon: ReactNode }
> = {
  "energy-star": {
    label: "Energy Star",
    colorClass: "bg-[#0469f0]",
    icon: (
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
      </svg>
    ),
  },
  "top-rated": {
    label: "Top Rated",
    colorClass: "bg-[#0469f0]",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  "lowest-price": {
    label: "Lowest Price",
    colorClass: "bg-[#ff9951]",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
      </svg>
    ),
  },
  "best-seller": {
    label: "Best Seller",
    colorClass: "bg-[#ff9951]",
    icon: null,
  },
  sale: {
    label: "Sale",
    colorClass: "bg-red-500",
    icon: null,
  },
  new: {
    label: "New",
    colorClass: "bg-gray-600",
    icon: null,
  },
  exclusive: {
    label: "Exclusive",
    colorClass: "bg-[#0469f0]",
    icon: null,
  },
}

export function BadgePill({ variant, label, colorClass, icon }: BadgePillProps) {
  if (variant === "custom") {
    return (
      <span
        className={`${colorClass || "bg-gray-600"} text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full flex items-center gap-1 w-fit`}
      >
        {icon}
        {label || "Badge"}
      </span>
    )
  }

  const config = VARIANT_CONFIG[variant]
  return (
    <span
      className={`${config.colorClass} text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-lg w-fit`}
    >
      {config.icon}
      {label || config.label}
    </span>
  )
}
